import { ReactNode } from "react";
import sanitizeAndParseInlineHTML from "@/utils/functions/sanitizeAndParseInlineHTML";
import { cn } from "@/lib/utils";

export interface BulletedTextListItemProps {
  header: string | ReactNode;
  description: string | ReactNode;
  icon?: "bullet-point" | "check" | "check-circle" | "none";
  variant?: "default" | "small";
  showHeaderColon?: boolean;
  itemClassName?: string;
}

const variantStyles = {
  default: {
    content: "elm-p5 leading-learn-hero-line-height",
    bulletPoint: "overflow-hidden mt-1",
    check: "overflow-hidden mt-1",
    checkCircle: "mt-2 mr-2 align-middle",
  },
  small: {
    content: "text-base",
    bulletPoint: "overflow-hidden",
    check: "overflow-hidden",
    checkCircle: "mt-1 align-middle",
  },
} as const;

export default function BulletedTextListItem({
  header,
  description,
  variant = "default",
  showHeaderColon = false,
  icon = "check-circle",
  itemClassName = "",
}: BulletedTextListItemProps) {
  const styles = variantStyles[variant];

  const parsedHeader =
    typeof header === "string" ? sanitizeAndParseInlineHTML(header) : header;
  const parsedDescription =
    typeof description === "string"
      ? sanitizeAndParseInlineHTML(description)
      : description;

  return (
    <li className="flex mb-3">
      {icon === "bullet-point" && (
        <div className="items-center pt-1 pr-4">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={styles.bulletPoint}
            color="#00262B"
          >
            <circle cx="8" cy="8" r="3" fill="currentColor" />
          </svg>
        </div>
      )}
      {icon === "check" && (
        <div className="items-center pr-4">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={styles.check}
            color="#00262B"
          >
            <path
              d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2Z"
              fill="currentColor"
            ></path>
          </svg>
        </div>
      )}
      {icon === "check-circle" && (
        <div className="items-center pr-4">
          <svg
            width="20"
            height="20"
            viewBox="0 0 512 512"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={styles.checkCircle}
            color="#00262B"
          >
            <path
              d="M256 8C119.033 8 8 119.033 8 256s111.033 248 248 248 248-111.033 248-248S392.967 8 256 8zm0 48c110.532 0 200 89.451 200 200 0 110.532-89.451 200-200 200-110.532 0-200-89.451-200-200 0-110.532 89.451-200 200-200m140.204 130.267l-22.536-22.718c-4.667-4.705-12.265-4.736-16.97-.068L215.346 303.697l-59.792-60.277c-4.667-4.705-12.265-4.736-16.97-.069l-22.719 22.536c-4.705 4.667-4.736 12.265-.068 16.971l90.781 91.516c4.667 4.705 12.265 4.736 16.97.068l172.589-171.204c4.704-4.668 4.734-12.266.067-16.971z"
              fill="currentColor"
            />
          </svg>
        </div>
      )}
      <div className={styles.content}>
        <b
          className={cn(
            "text-primary-500 [&_a]:text-link [&_a]:underline [&_a]:hover:text-link-hover",
            itemClassName,
          )}
        >
          {header && (
            <>
              {parsedHeader}
              {showHeaderColon && ": "}{" "}
            </>
          )}
        </b>
        <span
          className={cn(
            "text-gray-700 [&_a]:text-link [&_a]:underline [&_a]:hover:text-link-hover [&_p]:m-0 [&_p]:inline",
            itemClassName,
          )}
        >
          {parsedDescription}
        </span>
      </div>
    </li>
  );
}
